<template>
  <div>
    <div>
      <h3 class="mb-2">
        VendorProof
      </h3>

      <v-skeleton-loader type="table-row" color="secondary" v-if="!loaded" />
      <v-data-table
        class="main-table"
        :headers="vpeHeaders"
        :items="vpeItems"
        hide-default-footer
        disable-sort
        :options="tableOptions"
        no-data-text="No Subscription"
        v-show="loaded"
        v-if="!this.isFeatureEnabled('MAINTENANCE_MODE')"
      >
        <template v-slot:item="{ item, index }">
          <tr
            :key="`vpe-table-${index}`"
            :class="
              item.status === 'EXPIRED' && !item.autoRenew && 'error--text'
            "
          >
            <td>
              {{ item.clientName || item.subscriptionTier || "VendorProof" }}
            </td>

            <td>
              <span :class="`${getStatusColor(item.status)}--text`">
                {{ item.statusName }}
              </span>
            </td>

            <td>{{ item.startDate | formatDate("L") }}</td>
            <td>
              <span v-if="item.renewalDate">{{
                item.renewalDate | formatDate("L")
              }}</span>
            </td>

            <td>
              <v-switch
                :success="item.autoRenew"
                v-model="item.autoRenew"
                :label="item.autoRenew ? 'ON' : 'OFF'"
                @change="updateAutoRenew(item)"
                :loading="item.loading"
              ></v-switch>
            </td>
          </tr>
        </template>
      </v-data-table>
      <v-alert v-else type="warning">
        We are currently performing maintenance. Subscription information will
        be available when maintenance has concluded.
      </v-alert>
    </div>
    <div class="mt-8 px-16">
      <v-divider></v-divider>
    </div>

    <div class="mt-8" v-if="loaded">
      <h3 class="mb-2">VendorProof Employee Monitoring</h3>
      <template v-if="!vpemItems || !vpemItems.length">
        <v-divider></v-divider>
        <employee-monitoring-pitch
          location="Subscription Page"
          class="px-lg-10"
        />
      </template>

      <v-data-table
        v-else
        class="main-table"
        :headers="vpemHeaders"
        :items="vpemItems"
        hide-default-footer
        disable-sort
        :options="tableOptions"
      >
        <template v-slot:item="{ item, index }">
          <tr
            :key="`vpem-table-${index}`"
            :class="
              item.status === 'EXPIRED' && !item.autoRenew && 'error--text'
            "
          >
            <td>
              VendorProof Employee Monitoring
            </td>

            <td>
              <span :class="`${getStatusColor(item.status)}--text`">
                {{ item.statusName }}
              </span>
            </td>

            <td>{{ item.startDate | formatDate("L") }}</td>
            <td>{{ item.renewalDate | formatDate("L") }}</td>

            <td>
              <v-switch
                :success="item.autoRenew"
                v-model="item.autoRenew"
                :label="item.autoRenew ? 'ON' : 'OFF'"
                @change="updateAutoRenew(item)"
                :loading="item.loading"
              ></v-switch>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import hasStatusFactory from "@/mixins/has-status-factory";
import { subscriptionsAPI, subscriptionByIdAPI } from "@/services/api";
import { get, put } from "@/services/request";
import { formatDate } from "@/services/format";
import EmployeeMonitoringPitch from "@/components/EmployeeMonitoringPitch";
import hasNotifications from "@/mixins/has-notifications";
import CancelReasonForm from "./CancelReasonForm.vue";

const statusColorMap = {
  ACTIVE: "success",
  EXPIRED: "error",
  UNKNOWN: "warning",
  REFUNDED: "warning",
  WAIVER_PENDING: "warning",
  WAIVER_APPROVED: "success",
  WAIVER_NOT_APPROVED: "error"
};

const tableOptions = {
  itemsPerPage: -1
};

const headers = [
  { text: "Subscription Status", value: "status" },
  { text: "Enrollment Date", value: "enrollmentDate" },
  { text: "Renewal Date", value: "renewalDate" },
  { text: "Auto-Renew", value: "autoRenew", align: "center" }
];

const verbiagesMap = {
  VP_ENGAGE_CVC: {
    onNotification: `You have turned on Auto Renew for your VendorProof subscription. <br> Your account will be billed on the renewal date.`,
    offNotification: `You have turned off Auto-Renew for your VendorProof subscription. <br> You are at risk of non-compliance.`,
    offWarningTitle: `Are you sure you want to cancel your VendorProof subscription?`,
    offWarningDesc:
      "Turning off Auto-Renew for your VendorProof subscription could put you at risk for non-compliance. If you leave Auto-Renew on, we will alert you before your subscription is up for renewal. "
  },
  VP_EMPLOYEE_MONITORING: {
    onNotification:
      "You have re-activated your VendorProof Employee  Monitoring subscription.",
    offNotification:
      "You have turned off Auto-Renew for VendorProof Employee Monitoring. <br> You are at risk of non-compliance.",
    offWarningTitle:
      "You are turning off your VendorProof Employee Monitoring subscription.",
    offWarningDesc:
      "Turning off Auto-Renew on your VendorProof Employee Monitoring subscription puts you at risk for non-compliance with your customers."
  }
};

const getMsg = (msg, param) => (typeof msg === "function" ? msg(param) : msg);

export default {
  name: "Subscriptions",
  mixins: [hasStatusFactory(statusColorMap), hasNotifications],

  data: () => ({
    tableOptions,
    loaded: false,
    items: [],
    vpeHeaders: [{ text: "Type", value: "customer" }, ...headers],
    vpemHeaders: [{ text: "Account", value: "account" }, ...headers]
  }),
  computed: {
    vpeItems() {
      return this.items.filter(
        ({ subscriptionType }) => subscriptionType === "VP_ENGAGE_CVC"
      );
    },
    vpemItems() {
      return this.items.filter(
        ({ subscriptionType }) => subscriptionType === "VP_EMPLOYEE_MONITORING"
      );
    }
  },
  created() {
    this.loadData().finally(() => (this.loaded = true));
  },
  methods: {
    loadData() {
      return get(subscriptionsAPI).then(response => {
        if (response && response.data) {
          this.items = response.data;
        }
      });
    },
    doUpdateAutoRenew(item, cancelReason) {
      const { offNotification, onNotification } = verbiagesMap[
        item.subscriptionType
      ];
      const clientName = item.clientName;

      put(subscriptionByIdAPI.getUrl(item.id), {
        autoRenew: item.autoRenew,
        ...cancelReason
      })
        .then(() => {
          if (item.autoRenew) {
            this.showMessage(getMsg(onNotification, clientName));
          } else {
            this.showMessage({
              mode: "warning",
              text: getMsg(offNotification, clientName)
            });
          }
        })
        .finally(() => {
          this.loadData();
        });
    },
    updateAutoRenew(item) {
      this.logEvent("Clicked auto-renew toggle", {
        AutoRenew: item.autoRenew ? "on" : !item.autoRenew ? "off" : "unknown",
        Type: item.subscriptionType
      });

      this.$set(item, "loading", true);

      if (!item.autoRenew) {
        const { offWarningTitle, offWarningDesc } = verbiagesMap[
          item.subscriptionType
        ];

        const clientName = item.clientName;

        this.setAlert({
          title: getMsg(offWarningTitle, clientName),
          description: offWarningDesc,
          actionText: "Yes, Turn Off",
          cancelText: "Nevermind",
          cancelFn: () => {
            item.autoRenew = true;
            this.$set(item, "loading", false);
          },
          actionFn: cancelReason => this.doUpdateAutoRenew(item, cancelReason),
          ...(item.subscriptionType === "VP_ENGAGE_CVC" && {
            form: CancelReasonForm
          })
        });
      } else {
        this.doUpdateAutoRenew(item);
      }
    }
  },
  filters: {
    formatDate
  },
  components: {
    EmployeeMonitoringPitch
  }
};
</script>

<style lang="sass" scoped>
h3
  font-size: 20px
  font-weight: normal

.v-input--switch ::v-deep
  .v-input__slot
    justify-content: center
    .v-label
      position: absolute !important
      left: calc(50% - 3.5rem) !important
      &.success--text
        left: auto !important
        right: calc(50% - 3rem) !important

.make-payment-container
  display: flex
  padding-right: 8px
  justify-content: right
</style>
